import { useSignal } from "@preact/signals-react";
import { toast } from "react-toastify";
import CurrencyInput from "../../../molecules/CurrencyInput";
import TextEdit from "../../../molecules/TextEdit";
import { applyDateMask } from "../../../../utils/dateMask";
import { DisbursementItem } from "../types/operations";
import { isValidDate } from "../utils/operationHelpers";

interface Props {
    disbursements: DisbursementItem[];
    totalAmount: number;
    onTotalAmountChange: (value: number) => void;
    onAdd: (disbursement: DisbursementItem) => void;
    onRemove: (index: number) => void;
    onUpdate: (index: number, disbursement: DisbursementItem) => void;
    disabled?: boolean;
    maxDisbursements?: number;
}

const DisbursementManager: React.FC<Props> = ({
    disbursements,
    totalAmount,
    onTotalAmountChange,
    onAdd,
    onRemove,
    onUpdate,
    disabled = false,
    maxDisbursements = 2
}) => {
    const newDisbursement = useSignal<{ amount: number; date: string | null }>({
        amount: 0,
        date: new Date().toLocaleDateString('pt-BR')
    });
    const editingIndex = useSignal<number | null>(null);
    const editingDisbursement = useSignal<{ amount: number; date: string }>({ amount: 0, date: '' });

    const handleAdd = () => {
        if (disbursements.length >= maxDisbursements) {
            toast.error(`Máximo de ${maxDisbursements} desembolsos permitidos`);
            return;
        }
        if (newDisbursement.value.amount <= 0) {
            toast.error("Informe um valor maior que zero");
            return;
        }

        // Validar se Valor Nominal foi informado
        if (!totalAmount || totalAmount <= 0) {
            toast.error("Informe o Valor Nominal antes de adicionar desembolsos");
            return;
        }

        // Validar se soma dos desembolsos não excede o Valor Nominal
        const currentSum = disbursements.reduce((sum, d) => sum + d.amount, 0);
        const newSum = currentSum + newDisbursement.value.amount;
        
        if (newSum > totalAmount) {
            toast.error(`Soma dos desembolsos (R$ ${newSum.toFixed(2)}) não pode exceder o Valor Nominal (R$ ${totalAmount.toFixed(2)})`);
            return;
        }

        onAdd({
            amount: newDisbursement.value.amount,
            date: newDisbursement.value.date || new Date().toLocaleDateString('pt-BR')
        });

        newDisbursement.value = { amount: 0, date: new Date().toLocaleDateString('pt-BR') };
    };

    const handleEdit = (index: number) => {
        editingIndex.value = index;
        editingDisbursement.value = { ...disbursements[index] };
    };

    const handleSave = (index: number) => {
        if (editingDisbursement.value.amount <= 0) {
            toast.error("Informe um valor maior que zero");
            return;
        }

        // Validar soma excluindo o item sendo editado
        const currentSum = disbursements.reduce((sum, d, i) => i === index ? sum : sum + d.amount, 0);
        const newSum = currentSum + editingDisbursement.value.amount;
        
        if (newSum > totalAmount) {
            toast.error(`Soma dos desembolsos (R$ ${newSum.toFixed(2)}) não pode exceder o Valor Nominal (R$ ${totalAmount.toFixed(2)})`);
            return;
        }

        onUpdate(index, editingDisbursement.value);
        editingIndex.value = null;
    };

    const handleCancel = () => {
        editingIndex.value = null;
    };

    const sumDisbursements = disbursements.reduce((sum, d) => sum + d.amount, 0);

    return (
        <div>
            <h2 className="text-xl font-semibold mt-4 mb-4">Desembolsos</h2>

            <div className="grid grid-cols-12 gap-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                <div className="col-span-2">
                    <CurrencyInput
                        id="disbursement_amount"
                        label="Valor do Desembolso"
                        prefix="R$"
                        fractionDigits={2}
                        value={newDisbursement.value.amount}
                        onChange={(value) => {
                            newDisbursement.value = { ...newDisbursement.value, amount: value };
                        }}
                    />
                </div>
                <div className="col-span-2">
                    <TextEdit
                        id="disbursement_date"
                        label="Data do Desembolso"
                        value={newDisbursement.value.date || ''}
                        onChange={(e) => {
                            newDisbursement.value = { ...newDisbursement.value, date: applyDateMask(e.target.value) };
                        }}
                        onBlur={(e) => {
                            if (!isValidDate(e.target.value)) {
                                toast.error('Data do Desembolso inválida');
                                e.target.focus();
                            }
                        }}
                        placeholder="dd/mm/aaaa"
                    />
                </div>
                <div className="col-span-2 flex items-end">
                    <button
                        onClick={handleAdd}
                        className="btn-add mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={disabled || disbursements.length >= maxDisbursements}
                    >
                        Incluir
                    </button>
                </div>
            </div>

            <div className="mt-4 overflow-x-auto">
                <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Valor</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Data</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                        {disbursements.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                    Nenhum desembolso adicionado
                                </td>
                            </tr>
                        ) : (
                            disbursements.map((disbursement, index) => (
                                <tr key={`${index}-${disbursement.amount}-${disbursement.date}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {editingIndex.value === index ? (
                                            <CurrencyInput
                                                id={`edit_disbursement_amount_${index}`}
                                                label=""
                                                prefix="R$"
                                                fractionDigits={2}
                                                value={editingDisbursement.value.amount}
                                                onChange={(value) => {
                                                    editingDisbursement.value = { ...editingDisbursement.value, amount: value };
                                                }}
                                            />
                                        ) : (
                                            `R$ ${disbursement.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
                                        )}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {editingIndex.value === index ? (
                                            <TextEdit
                                                id={`edit_disbursement_date_${index}`}
                                                label=""
                                                value={editingDisbursement.value.date}
                                                onChange={(e) => {
                                                    editingDisbursement.value = { ...editingDisbursement.value, date: applyDateMask(e.target.value) };
                                                }}
                                                placeholder="dd/mm/aaaa"
                                            />
                                        ) : (
                                            disbursement.date
                                        )}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                        {editingIndex.value === index ? (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleSave(index)}
                                                    className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                                                >
                                                    Salvar
                                                </button>
                                                <button
                                                    onClick={handleCancel}
                                                    className="bg-gray-500 text-white px-2 py-1 rounded text-xs hover:bg-gray-600"
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEdit(index)}
                                                    className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={disabled}
                                                >
                                                    Editar
                                                </button>
                                                <button
                                                    onClick={() => onRemove(index)}
                                                    className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={disabled}
                                                >
                                                    Remover
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-2">
                    <CurrencyInput
                        id="disbursement_totalAmount"
                        label="Valor Nominal *"
                        prefix="R$"
                        fractionDigits={2}
                        value={totalAmount}
                        onChange={onTotalAmountChange}
                        disabled={false}
                    />
                </div>
                <div className="col-span-2">
                    <CurrencyInput
                        id="disbursement_sum"
                        label="Soma dos Desembolsos"
                        prefix="R$"
                        fractionDigits={2}
                        value={sumDisbursements}
                        disabled={true}
                    />
                </div>
                <div className="col-span-2">
                    <CurrencyInput
                        id="disbursement_discount"
                        label="Valor de Abatimento"
                        prefix="R$"
                        fractionDigits={2}
                        value={totalAmount - sumDisbursements}
                        disabled={true}
                    />
                </div>
            </div>
        </div>
    );
};

export default DisbursementManager;
